'use client';
import { MultiServicePreset } from '@/types';

import { useEffect, useState } from 'react';

import Hero from '@/app/(with-metadata)/(main)/Hero';
import Resources from '@/app/(with-metadata)/(main)/ResourcesSection';
import ResourcesSectionMulti from '@/app/(with-metadata)/(main)/ResourcesSectionMulti';
import NotesAndGoogle from '@/components/NotesAndGoogle';
import TextOneSection from '@/app/(with-metadata)/(main)/TextOneSection';
import TextTwoSection from '@/app/(with-metadata)/(main)/TextTwoSection';
import MediaSection from '@/app/(with-metadata)/(main)/MediaSection';
import { CardDefinition, ContentType } from '@/types';
import { useConfig } from '../../contexts/ConfigContext';
import {
  getFrontPageMultiTexts,
  getFrontPageTexts,
} from '@/services/server/text';
import { getFrontPageMedia } from '@/services/server/media';
import DownloadSection from './DownloadSection';
import { useAuth } from '@/app/contexts/AuthContext';
import { checkUrlCountry, isFRAndAllopassInternetPlus } from '@/helpers';
import AccountSection from './AccountSection';
import MediaSectionMultiFitnessYogaPilates from './MediaSectionMultiFitnessYogaPilates';
import MediaSectionMultiAstrologyMeditationLove from './MediaSectionMultiAstrologyMeditationLove';

export default function Home() {
  const { selectedLanguage, config } = useConfig();
  const { isAuth } = useAuth();

  const [videos, setVideos] = useState<CardDefinition[]>([]);
  const [videosMultiFitnessYogaPilates, setVideosMultiFitnessYogaPilates] =
    useState<CardDefinition[][]>([]);
  const [
    mediaMultiAstrologyMeditationLove,
    setMediaMultiAstrologyMeditationLove,
  ] = useState<CardDefinition[][]>([]);
  const [posts, setPosts] = useState<CardDefinition[]>([]);
  const [isPortalFRAPInternetPlus, setIsPortalFRAPInternetPlus] =
    useState(false);
  const [isPortalFR, setIsPortalFR] = useState(false);

  useEffect(() => {
    const fetchData = () => {
      // MEDIA
      let fitnessItems: CardDefinition[] = [];
      let yogaItems: CardDefinition[] = [];
      let pilatesItems: CardDefinition[] = [];
      if (config.multiService === MultiServicePreset.FYP) {
        // Videos for each services
        try {
          try {
            // fitness
            if (
              config.multiServiceTypes?.[0] &&
              selectedLanguage &&
              config.contents.media
            ) {
              fitnessItems = getFrontPageMedia(
                config.multiServiceTypes[0],
                selectedLanguage,
                config.contents.media,
              );
            }
          } catch (error) {
            console.error(
              '[Home] Failed to load multi services media 1:',
              error,
            );
          }
          try {
            // yoga
            if (
              config.multiServiceTypes?.[1] &&
              selectedLanguage &&
              config.contents.media
            ) {
              yogaItems = getFrontPageMedia(
                config.multiServiceTypes[1],
                selectedLanguage,
                config.contents.media,
              );
            }
          } catch (error) {
            console.error(
              '[Home] Failed to load multi services media 2:',
              error,
            );
          }
          try {
            // pilates
            if (
              config.multiServiceTypes?.[2] &&
              selectedLanguage &&
              config.contents.media
            ) {
              pilatesItems = getFrontPageMedia(
                config.multiServiceTypes[2],
                selectedLanguage,
                config.contents.media,
              );
            }
          } catch (error) {
            console.error(
              '[Home] Failed to load multi services media 3:',
              error,
            );
          }
        } catch (error) {
          console.error('[Home] Failed to load multi services medias', error);
        } finally {
          setVideosMultiFitnessYogaPilates([
            fitnessItems ?? [],
            yogaItems ?? [],
            pilatesItems ?? [],
          ]);
        }
      } else if (config.multiService === MultiServicePreset.AML) {
        let astrologyItems: CardDefinition[] = [];
        let meditationItems: CardDefinition[] = [];
        let loveItems: CardDefinition[] = [];

        try {
          try {
            if (
              config.multiServiceTypes?.[0] &&
              selectedLanguage &&
              config.contents.media
            ) {
              astrologyItems = getFrontPageMedia(
                config.multiServiceTypes[0],
                selectedLanguage,
                config.contents.media,
              );
            }
          } catch (error) {
            console.error('[Home] Failed to load AML media 1:', error);
          }
          try {
            if (
              config.multiServiceTypes?.[1] &&
              selectedLanguage &&
              config.contents.media
            ) {
              meditationItems = getFrontPageMedia(
                config.multiServiceTypes[1],
                selectedLanguage,
                config.contents.media,
              );
            }
          } catch (error) {
            console.error('[Home] Failed to load AML media 2:', error);
          }
          try {
            if (
              config.multiServiceTypes?.[2] &&
              selectedLanguage &&
              config.contents.media
            ) {
              loveItems = getFrontPageMedia(
                config.multiServiceTypes[2],
                selectedLanguage,
                config.contents.media,
              );
            }
          } catch (error) {
            console.error('[Home] Failed to load AML media 3:', error);
          }
        } catch (error) {
          console.error('[Home] Failed to load AML medias', error);
        } finally {
          setMediaMultiAstrologyMeditationLove([
            astrologyItems ?? [],
            meditationItems ?? [],
            loveItems ?? [],
          ]);
        }
      } else {
        // Mono service videos
        try {
          if (config.serviceType && selectedLanguage && config.contents.media) {
            const media = getFrontPageMedia(
              config.serviceType,
              selectedLanguage,
              config.contents.media,
            );
            setVideos(media);
          }
        } catch (error) {
          console.error('[Home] Failed to load media:', error);
        }
      }

      // TEXTS
      try {
        if (config.serviceType && selectedLanguage && config.contents.text2) {
          const multiServiceTypes = config.multiServiceTypes;

          // Front page text2 (historicly "articles")
          const posts = multiServiceTypes
            ? getFrontPageMultiTexts(
                multiServiceTypes,
                selectedLanguage,
                config.contents.text2 as ContentType,
                'text2',
              )
            : getFrontPageTexts(
                config.serviceType,
                selectedLanguage,
                config.contents.text2 as ContentType,
                'text2',
              );
          setPosts(posts);
        }
      } catch (error) {
        console.error('[Home] Failed to load text2:', error);
      }
    };

    fetchData();

    // Identify FR portals for account page
    if (
      isFRAndAllopassInternetPlus(config.SERVICE_URL_LINK) || // real condition
      checkUrlCountry(config.SERVICE_URL_LINK, 'testing.') // for the testing env
    ) {
      setIsPortalFRAPInternetPlus(true);
    }
    // Identify FR portals for download
    if (checkUrlCountry(config.SERVICE_URL_LINK, 'fr')) {
      setIsPortalFR(true);
    }
  }, [
    config.serviceType,
    config.multiService,
    config.multiServiceTypes,
    selectedLanguage,
    config.SERVICE_URL_LINK,
    config.contents.text2,
    config.contents.media,
  ]);

  return (
    <main className='max-w-[1920px] mx-auto overflow-hidden'>
      <Hero />
      <section className='sm:max-w-[1400px] max-w-[390px] mx-auto px-4'>
        {isPortalFRAPInternetPlus && <AccountSection />}
        {isPortalFR && isAuth && <DownloadSection />}
        {config && !config.multiService && <Resources />}
        {config && config.multiService && <ResourcesSectionMulti />}
        <NotesAndGoogle />
        {config && !config.multiService && <MediaSection items={videos} />}
        {config && config.multiService === MultiServicePreset.FYP && (
          <MediaSectionMultiFitnessYogaPilates
            items={videosMultiFitnessYogaPilates}
          />
        )}
        {config && config.multiService === MultiServicePreset.AML && (
          <MediaSectionMultiAstrologyMeditationLove
            items={mediaMultiAstrologyMeditationLove}
          />
        )}
        {config && config.contents.text2 && <TextTwoSection items={posts} />}
        {config && config.contents.text1 && <TextOneSection />}
      </section>
    </main>
  );
}
